import { useState, useCallback } from 'react';
import * as validationUtils from './validation';

// Custom hook for form validation
export const useValidation = (initialValues = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Validate a single field
  const validateField = useCallback((name, value) => {
    const rules = validationRules[name];
    if (!rules) return null;

    // Required validation
    if (rules.required && !validationUtils.isRequired(value)) {
      return validationUtils.validationMessages.required;
    }

    // Custom validations
    if (rules.email && !validationUtils.isValidEmail(value)) {
      return validationUtils.validationMessages.email;
    }

    if (rules.password && !validationUtils.isValidPassword(value)) {
      return validationUtils.validationMessages.password;
    }

    if (rules.companyName && !validationUtils.isValidCompanyName(value)) {
      return validationUtils.validationMessages.companyName;
    }

    if (rules.name && !validationUtils.isValidName(value)) {
      return validationUtils.validationMessages.name;
    }

    if (rules.phoneNumber && !validationUtils.isValidPhoneNumber(value)) {
      return validationUtils.validationMessages.phoneNumber;
    }

    if (rules.message && !validationUtils.isValidMessage(value)) {
      return validationUtils.validationMessages.message;
    }

    if (rules.url && !validationUtils.isValidURL(value)) {
      return validationUtils.validationMessages.url;
    }

    if (rules.min && !validationUtils.hasMinLength(value, rules.min)) {
      return validationUtils.validationMessages.minLength(rules.min);
    }

    if (rules.max && !validationUtils.hasMaxLength(value, rules.max)) {
      return validationUtils.validationMessages.maxLength(rules.max);
    }

    if (rules.number && !validationUtils.isNumber(value)) {
      return validationUtils.validationMessages.number;
    }

    if (rules.positiveNumber && !validationUtils.isPositiveNumber(value)) {
      return validationUtils.validationMessages.positiveNumber;
    }

    // Custom validator function
    if (typeof rules.validator === 'function') {
      const result = rules.validator(value, values);
      if (result !== true) {
        return result;
      }
    }

    return null;
  }, [validationRules, values]);

  // Validate all fields
  const validateForm = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    Object.keys(validationRules).forEach(fieldName => {
      const value = values[fieldName];
      const error = validateField(fieldName, value);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [values, validationRules, validateField]);

  // Handle input changes
  const handleChange = useCallback((name, value) => {
    setValues(prev => ({ ...prev, [name]: value }));

    // If field was touched, validate it on change
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  }, [touched, validateField]);

  // Handle field blur (mark as touched)
  const handleBlur = useCallback((name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const value = values[name];
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  }, [values, validateField]);

  // Reset form
  const reset = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
  }, [initialValues]);

  // Set form values programmatically
  const setFormValues = useCallback((newValues) => {
    setValues(newValues);
  }, []);

  return {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateForm,
    reset,
    setFormValues
  };
};

// Predefined validation rule sets
export const validationRules = {
  login: {
    email: { required: true, email: true },
    password: { required: true, min: 6 }
  },
  register: {
    companyName: { required: true, companyName: true },
    email: { required: true, email: true },
    password: { required: true, password: true },
    confirmPassword: { 
      required: true,
      validator: (value, formValues) => {
        if (value !== formValues.password) {
          return validationUtils.validationMessages.passwordMatch;
        }
        return true;
      }
    }
  },
  contact: {
    name: { required: true, name: true },
    email: { required: true, email: true },
    message: { required: true, message: true }
  },
  phone: {
    phoneNumber: { required: true, phoneNumber: true }
  }
};